import React, { useEffect, useMemo, useState } from 'react'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import queryString, { ParsedQuery } from 'query-string'
import { useLocation, useNavigate } from 'react-router-dom'
import Dropdown from 'react-bootstrap/Dropdown'
import {
  AssigneeTab,
  AssigneeTypeTabs,
} from 'src/components/Reusable/Dropdown/AssigneeTypeTabs'
import {
  UserRecord,
  UserState,
} from '@trustero/trustero-api-web/lib/account/account_pb'
import { useFilterLabels } from '../FilterLabelsContext'
import { useUsers } from '../../../../async/Users'
import { FilterName, FilterParam } from '../FilterBar.types'
import { Spinner } from '../../../../../Throbber'
import { CheckBoxInput } from '../../../Inputs/CheckBox'
import { MenuPlaceholder } from '../../../../Placeholders/MenuPlaceholder'
import {
  FilterCountChip,
  FilterDropdownItem,
  FilterDropdownToggle,
} from '../FilterBar.styles'
import { Owner } from '../../../../../Utils/globalEnums'
import { DropDownMenu } from './FilterDropdown.styles'

export type OwnerGroupOption = { value: string; label: string }
const NO_GROUPS: OwnerGroupOption[] = []

const OwnerItem = ({
  name,
  email,
  id,
  searchParams,
  appliedFilters,
  filterParam,
  requiredCapabilities = [CAPABILITY.CAN_VIEW_USERS],
}: {
  name?: string
  email: string
  id: string
  searchParams: ParsedQuery
  appliedFilters: string[]
  filterParam: FilterParam
  requiredCapabilities?: CAPABILITY[]
}): JSX.Element => {
  const navigate = useNavigate()
  const location = useLocation()
  const isRequestsPage = location.pathname.includes('requests')
  const userArg = isRequestsPage ? id : `${email?.toLowerCase()}`
  const newAppliedFilters = new Set(appliedFilters.map((f) => f.toLowerCase()))
  const isChecked = newAppliedFilters.has(userArg)
  isChecked ? newAppliedFilters.delete(userArg) : newAppliedFilters.add(userArg)

  const href = queryString.stringifyUrl(
    {
      url: location.pathname,
      query: {
        ...searchParams,
        [filterParam]: Array.from(newAppliedFilters),
      },
    },
    { arrayFormat: 'bracket' },
  )

  return (
    <FilterDropdownItem replace to={href}>
      <CheckBoxInput
        id={`${filterParam}-${email}`}
        label={name || email}
        checked={isChecked}
        onClick={(e) => {
          e.stopPropagation()
        }}
        onChange={() => {
          navigate(href, { replace: true })
        }}
        tabIndex={-1}
        requiredCapabilities={requiredCapabilities}
      />
    </FilterDropdownItem>
  )
}

export const OwnerFilterDropdown = React.memo(
  ({
    customName,
    includeUnassigned = true,
    isNested = false,
    filterParam = FilterParam.OWNER,
    filterName = FilterName.OWNER,
    groups = NO_GROUPS,
  }: {
    customName?: string
    includeUnassigned?: boolean
    isNested?: boolean
    filterParam?: FilterParam
    filterName?: FilterName
    // Assignable user groups, listed after the users. Their values are passed
    // through to the URL as given.
    groups?: OwnerGroupOption[]
  }): JSX.Element => {
    const location = useLocation()
    const userResp = useUsers()
    const { registerFilter } = useFilterLabels()
    // With groups, users and groups are tabs; null until the person picks one.
    const [pickedTab, setPickedTab] = useState<AssigneeTab | null>(null)

    useEffect(() => {
      const users = userResp.data?.getUsersList() ?? []
      if (users.length === 0 && groups.length === 0) return
      const valueMap: Record<string, string> = {
        [Owner.UNASSIGNED_BE]: Owner.UNASSIGNED,
      }
      users.forEach((u) => {
        const display = u.getName() || u.getEmail()
        valueMap[u.getEmail()] = display
        valueMap[u.getId()] = display
      })
      groups.forEach(({ value, label }) => {
        valueMap[value] = label
      })
      registerFilter(filterParam, filterName, valueMap)
    }, [userResp.data, groups, registerFilter, filterParam, filterName])

    const searchParams = useMemo(() => {
      return queryString.parse(location.search, {
        arrayFormat: 'bracket',
      })
    }, [location])

    const appliedFilters = useMemo(() => {
      const preprocessed = searchParams[filterParam]
      return Array.isArray(preprocessed)
        ? preprocessed
        : preprocessed
        ? [preprocessed]
        : []
    }, [searchParams, filterParam])

    if (userResp.error) return <></>
    // Not loading and no data means the caller can't list users; any groups
    // still show.
    if (userResp.isLoading) {
      return (
        <>
          <Dropdown.Toggle
            as={FilterDropdownToggle}
            id={`${filterName}-filter-dropdown`}
            $isNested={isNested}
          >
            <p>{customName || filterName}</p>
            <Spinner size="s" color="primary" mx="xxs" />
          </Dropdown.Toggle>
          <DropDownMenu>
            <MenuPlaceholder height={108} />
          </DropDownMenu>
        </>
      )
    }
    const users =
      userResp.data
        ?.getUsersList()
        .filter((user) => user.getState() === UserState.USER_ACTIVE) ?? []
    // Opens on what is selected: the groups tab when only groups are, or when
    // the caller cannot list users.
    const groupValues = new Set(groups.map(({ value }) => value))
    const opensOnGroups =
      users.length === 0 ||
      (appliedFilters.length > 0 &&
        appliedFilters.every((value) => groupValues.has(value)))
    const activeTab: AssigneeTab =
      groups.length === 0
        ? 'users'
        : pickedTab ?? (opensOnGroups ? 'groups' : 'users')

    return (
      <Dropdown autoClose="outside">
        <Dropdown.Toggle
          as={FilterDropdownToggle}
          id={`${filterName}-filter-dropdown`}
          $hasSelection={appliedFilters.length > 0}
          $isNested={isNested}
        >
          <p>{customName || filterName}</p>
          {appliedFilters.length > 0 && (
            <FilterCountChip ml="xxs">{appliedFilters.length}</FilterCountChip>
          )}
        </Dropdown.Toggle>
        <DropDownMenu>
          {groups.length > 0 && (
            <AssigneeTypeTabs active={activeTab} onChange={setPickedTab} />
          )}
          {activeTab === 'users' && includeUnassigned && (
            <OwnerItem
              key={Owner.UNASSIGNED}
              name={Owner.UNASSIGNED}
              email={Owner.UNASSIGNED_BE}
              id={Owner.UNASSIGNED_BE}
              searchParams={searchParams}
              appliedFilters={appliedFilters}
              filterParam={filterParam}
              requiredCapabilities={[]}
            />
          )}
          {activeTab === 'users' &&
            users.map((user: UserRecord) => {
              return (
                <OwnerItem
                  key={user.getEmail()}
                  name={user.getName()}
                  email={user.getEmail()}
                  id={user.getId()}
                  searchParams={searchParams}
                  appliedFilters={appliedFilters}
                  filterParam={filterParam}
                />
              )
            })}
          {activeTab === 'groups' &&
            groups.map(({ value, label }) => (
              <OwnerItem
                key={value}
                name={label}
                email={value}
                id={value}
                searchParams={searchParams}
                appliedFilters={appliedFilters}
                filterParam={filterParam}
                requiredCapabilities={[]}
              />
            ))}
        </DropDownMenu>
      </Dropdown>
    )
  },
)
